import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface SpotsMapEmailGateProps {
  onSubmit: () => void;
}

const SpotsMapEmailGate = ({ onSubmit }: SpotsMapEmailGateProps) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await supabase.from('deck_views').insert({
        email: trimmed,
        deck_type: 'spots_map',
        slides_viewed: [],
        total_slides_viewed: 0,
        duration_seconds: 0,
        user_agent: navigator.userAgent,
      });
    } catch (err) {
      // Non-blocking — still let them through
    }

    localStorage.setItem('spotsMapEmail', trimmed);
    setIsClosing(true);
    setTimeout(() => onSubmit(), 500);
    setLoading(false);
  };

  return (
    <AnimatePresence>
      {!isClosing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-background rounded-3xl w-full max-w-[340px] sm:max-w-sm shadow-2xl overflow-hidden"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
          >
            <div className="px-6 pt-8 pb-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <MapPin className="w-7 h-7 text-primary" />
              </div>
              <h2 className="font-funnel text-xl font-bold text-foreground mb-1">
                Looking for a quiet spot?
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Browse our curated map of the best work-friendly spots in London. Enter your email to explore.
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3.5 bg-muted rounded-2xl text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 text-center"
                  autoFocus
                />
                {error && <p className="text-xs text-red-500">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-primary text-primary-foreground rounded-full text-sm font-semibold disabled:opacity-50 transition-opacity"
                >
                  {loading ? 'Loading...' : 'Explore the map'}
                </button>
              </form>

              <p className="text-[10px] text-muted-foreground/60 mt-4 leading-relaxed">
                This information will be shared with qwip ltd.{' '}
                <a href="/privacy" className="underline" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SpotsMapEmailGate;
